<template>
  <div>
    <TitleBar>
      <template v-slot:title><translate>Revenue and Expenses</translate></template>
      <template v-slot:buttons>
        <GekkoButton :text="$gettext('Download')" icon="fa fa-download" variant="outline-primary" @click="downloadCSV" class="mr-2" />
        <GekkoButton :text="$gettext('All reports')" :to="{ name: 'Reports' }" icon="fa fa-chart-column" />
      </template>
    </TitleBar>

    <div class="content">
      <FilterBar
        cookieName="RevenueExpensesFilterOptionsVue"
        :mainFilters="['RevenueExpensesDateRangeSelector']"
        filterBarType="revenue-expenses"
        @change="filtersChanged"
      >
      </FilterBar>
      <div class="py-4">
        <CashflowReportChart :chartData="chartData" :loading="loading" />
      </div>
      <RevenueExpensesTable :revenueData="revenueData" />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import TitleBar from '@/components/TitleBar'
import FilterBar from '@/components/list_filters/FilterBar'
import RevenueExpensesTable from '@/components/cash_flow/RevenueExpensesTable'
import CashflowReportChart from '@/components/cash_flow/CashflowReportChart'
import GekkoButton from '@/components/GekkoButton'
import ReportsService from '@/store/services/reports.service'

export default {
  name: 'CashFlow',
  components: { TitleBar, FilterBar, GekkoButton, RevenueExpensesTable, CashflowReportChart },
  data() {
    return {
      groupBy: 'quarter',
      selectedMonth: 0,
      selectedYear: new Date().getFullYear(),
      revenueData: [],
      chartData: null,
      loading: null,
    }
  },
  computed: {
    ...mapGetters({
      getCompanyId: 'main/getCompanyId',
    }),
  },
  methods: {
    getData() {
      this.chartData = null
      this.loading = true

      var startEndDateInterval = this._interval(this.selectedYear)
      var start_date = startEndDateInterval.start_date, end_date = startEndDateInterval.end_date

      ReportsService.getRevenueExpensesData({
        company_profile: this.getCompanyId,
        start_date: start_date,
        end_date: end_date,
        group_by: this.groupBy
      }).then((response) => {
        this.revenueData = response.data
        this._convertToChartData()
        this.loading = false
      }).catch(() => {
        this.revenueData = []
        this.chartData = null
      }).finally(() => {
        this.loading = false
      })
    },
    _convertToChartData() {
      const revenueDataExclYear = this.revenueData.map((row) => (Array.isArray(row) ? row.slice(0, -1) : row))
      
      this.chartData = {
        labels: this.revenueData[0].slice(1, -1), // Exclude first column (labels) and last column (year total)

        datasets: [
          {
            type: 'line',
            label: this.$gettext('Margin'),
            data: revenueDataExclYear.find((x) => x[0] === 'margin').slice(1),
            borderColor: '#37A0CD',
          },
          {
            type: 'bar',
            label: this.$gettext('Unpaid invoices'),
            data: revenueDataExclYear.find((x) => x[0] === 'unpaidincome').slice(1),
            backgroundColor: '#22ac8A',
            stack: 'a',
          },
          {
            type: 'bar',
            label: this.$gettext('Paid invoices'),
            data: revenueDataExclYear.find((x) => x[0] === 'income').slice(1),
            backgroundColor: '#22ac8A',
            stack: 'b',
          },
          {
            type: 'bar',
            label: this.$gettext('Net expenses'),
            data: revenueDataExclYear.find((x) => x[0] === 'netexpenses').slice(1),
            backgroundColor: '#ac8255',
            stack: 'c',
          }
        ],
      }
    },
    _interval(selectedOption) {
      var interval = {}
      var year = parseInt(selectedOption)
      interval.start_date = new Date(year, 0, 1).toString('yyyy-MM-dd')
      interval.end_date = new Date(year, 11, 31).toString('yyyy-MM-dd')
      return interval
    },
    filtersChanged(filters) {
      if (filters.revenue_expenses_options) {
        this.groupBy = filters.revenue_expenses_options.groupBy
        this.selectedYear = filters.revenue_expenses_options.year
      } else {
        this.groupBy = 'quarter'
        this.selectedYear = new Date().getFullYear()
      }
      this.getData()
    },
    downloadCSV() {
      let startEndDateInterval = this._interval(this.selectedYear)
      let start_date = startEndDateInterval.start_date, end_date = startEndDateInterval.end_date
      
      ReportsService.downloadRevenueExpensesCSV({
        company_profile: this.getCompanyId,
        start_date: start_date,
        end_date: end_date,
        group_by: this.groupBy
      }).then(() => {
        this.$store.commit('setAlert', {
            title: this.$gettext('Report Download'),
            text: this.$gettext('Your download is being prepared, check your downloads page to download the file.'),
            variant: 'info',
            duration: 4
          })
        if (this.$route.name === "ControlPanelInvoices") {
          this.$router.push({ name: 'ControlPanelFiles' })
        } else {
          this.$router.push({ name: 'Files' })
        }
      }).catch(() => {
        this.$store.commit('setAlert', {
          title: this.$gettext('Report Download Error'),
          text: this.$gettext('An error occurred while trying to download the report. Please try again later.'),
          variant: 'danger',
          duration: 4
        })
      })
    }
  }
}
</script>